import { useEffect, useMemo } from 'react';
import { getConfig } from '../AppConfig';
import TEMPLATES, {
  TEMPLATE_NAMES,
} from '../components/page/RestaurantLanding/templates';
import { getText } from '../util/Helpers';
import {
  hexToRelativeLuminance,
  contrastRatio,
  applyBrandColourGuard,
} from '../util/colourUtils';

// Re-export the pure helpers so that any code that imported them directly
// from this module continues to work without changes.
export { hexToRelativeLuminance, contrastRatio };

const FONT_LINK_ID = 'restaurant-template-font';

/** Fallback accent used by the Bold template when no brand colour is configured. */
const BOLD_ACCENT_FALLBACK = '#E63012';

/**
 * Returns the active restaurant visual template token object.
 *
 * The active template is read from the `site_template` AppConfig locale key
 * (e.g. "ember", "garden", "bold", "classic").  Falls back to "classic" when
 * the key is absent or unrecognised.
 *
 * For the Bold template the `primary` accent colour is pulled from the
 * restaurant's configured `primary_color` AppConfig locale key so the site
 * feels truly on-brand.  When that key is absent the hardcoded fallback
 * `#E63012` is used instead.
 *
 * Contrast guard: any template that resolves its accent from a restaurant-
 * supplied `primary_color` passes through `applyBrandColourGuard`.  If the
 * resolved accent has a contrast ratio below 3:1 against the template
 * background, `primaryText` is switched to `#000000` so that buttons and
 * accents remain WCAG AA–readable.  The accent colour itself is preserved so
 * the brand identity is not altered.
 *
 * As a side-effect, injects the template's Google Fonts <link> into
 * <head> so the correct typeface is available to the page.
 */
function useRestaurantTemplate() {
  const config = getConfig();

  const templateName = useMemo(() => {
    const raw = getText(config, 'site_template', TEMPLATE_NAMES.classic);
    return TEMPLATES[raw] ? raw : TEMPLATE_NAMES.classic;
  }, [config]);

  const template = useMemo(() => {
    const base = TEMPLATES[templateName];

    if (templateName === TEMPLATE_NAMES.bold) {
      const brandColor = getText(config, 'primary_color', BOLD_ACCENT_FALLBACK);
      // Only override when we actually received a non-empty colour value.
      const accent =
        brandColor && brandColor !== BOLD_ACCENT_FALLBACK
          ? brandColor
          : BOLD_ACCENT_FALLBACK;

      // Delegate to the shared guard — compares accent against white (#FFFFFF,
      // the Bold template background) and adjusts primaryText if needed.
      return applyBrandColourGuard(base, accent, base.background);
    }

    return base;
  }, [templateName, config]);

  // Inject / swap the Google Fonts <link> whenever the template changes.
  // Uses the media="print" trick so the stylesheet is fetched non-blocking
  // and then switched to media="all" once loaded — matching the pattern
  // recommended by web.dev/defer-non-critical-css.
  useEffect(() => {
    if (!template.googleFontsUrl) return;

    // Remove any pre-existing managed font link (covers template swap).
    const existing = document.getElementById(FONT_LINK_ID);
    if (existing) existing.remove();

    // Non-blocking <link>: starts as print-only so the browser fetches the
    // stylesheet without blocking the render path, then switches to "all"
    // once the sheet has loaded.
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.media = 'print';
    link.href = template.googleFontsUrl;
    link.onload = () => {
      link.media = 'all';
    };
    document.head.appendChild(link);

    // <noscript> fallback so browsers with JS disabled still get the font.
    const noscriptId = `${FONT_LINK_ID}-noscript`;
    const existingNoscript = document.getElementById(noscriptId);
    if (existingNoscript) existingNoscript.remove();
    const noscript = document.createElement('noscript');
    noscript.id = noscriptId;
    noscript.innerHTML = `<link rel="stylesheet" href="${template.googleFontsUrl}">`;
    document.head.appendChild(noscript);
  }, [template.googleFontsUrl]);

  return template;
}

export default useRestaurantTemplate;
